// Back closes what is on top, and never leaves the world.
//
// On a phone, Back is how anything is put away: Android's back button or
// edge swipe, Safari's swipe in from the left. To the browser all of these
// are a step back through the page's history, and with nothing in the way
// that step leaves the viewer, and the session with it. A left thumb on
// the stick is never far from the edge the swipe starts at.
//
// So while someone is in the world on a touch screen, the page keeps one
// history entry of its own on top, the guard. Back steps off it; the
// viewer puts it straight back and closes whatever is on top instead. Each
// thing that can be put away, a sheet, the pie, a menu, an open window,
// the chat field, goes on the stack with `pushLayer` as it opens and comes
// off with the release it was given when it closes some other way.
// Anything that should know when something opens over it listens with
// `onLayerOpen`, as the chat field does to get out of the way. With
// nothing open, Back does nothing at all unless an idle action has been
// set with `setIdleBack`. There is only ever the one guard entry, however
// many layers are open, so no entry is ever left behind for Back to land
// on later.
//
// Browsers do not let a page hold Back for ever: Chrome steps over an
// entry the page added without the user having touched it since. The
// guard is put back the moment Back is pressed, and the next touch on the
// page makes it count again, so only a second Back pressed before touching
// anything gets past it. On a desktop there is no guard, and Back leaves
// the page as it always has; layers may still be pushed there, harmlessly.

import { device, onDeviceChange } from "@/input/device";

/** The marker on the guard's history entry. */
const GUARD = "primverse-guard";

/** Something on the stack: take it off when it closes by other means, or bring it back to the top. */
export interface Layer {
  release(): void;
  raise(): void;
}

/** The parts of History the stack uses, so tests can give it their own. */
export interface HistoryLike {
  readonly state: unknown;
  pushState(data: unknown, unused: string): void;
  back(): void;
}

/** Where popstate is heard: the window. */
export interface PopSource {
  addEventListener(type: "popstate", fn: (e: { state: unknown }) => void): void;
  removeEventListener(type: "popstate", fn: (e: { state: unknown }) => void): void;
}

export function isGuard(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as Record<string, unknown>)[GUARD] === true;
}

export class BackStack {
  private layers: Array<{ close: () => void }> = [];
  private armed = false;
  private pushWatchers = new Set<() => void>();
  /** What Back does with nothing open while the guard is up. */
  onIdle: (() => void) | null = null;

  constructor(
    private history: HistoryLike,
    private source: PopSource,
  ) {}

  /** How many layers are open. */
  get size(): number {
    return this.layers.length;
  }

  /** Whether the guard is up. */
  get guarding(): boolean {
    return this.armed;
  }

  /**
   * Put something on top. `close` is what Back does to it. The layer is
   * taken off before `close` runs, so close may release it again without
   * harm, and something that decides not to close pushes itself again.
   */
  push(close: () => void): Layer {
    const entry = { close };
    this.layers.push(entry);
    for (const fn of [...this.pushWatchers]) fn();
    return {
      release: () => {
        const at = this.layers.indexOf(entry);
        if (at >= 0) this.layers.splice(at, 1);
      },
      raise: () => {
        const at = this.layers.indexOf(entry);
        if (at < 0) return;
        this.layers.splice(at, 1);
        this.layers.push(entry);
      },
    };
  }

  /**
   * Be told each time something goes on the stack, which is something
   * opening over whatever was there. Told before `push` returns, so whoever
   * is pushing has not yet got its layer back. Returns a way to stop.
   */
  onPush(fn: () => void): () => void {
    this.pushWatchers.add(fn);
    return () => this.pushWatchers.delete(fn);
  }

  /** Close the top layer, as Back does. Whether there was one. */
  closeTop(): boolean {
    const top = this.layers.pop();
    if (!top) return false;
    top.close();
    return true;
  }

  /**
   * Put the guard up or take it down. Up, the page gets its own entry on
   * top of the history, unless it is already on one (a reload keeps the
   * entry it was on). Down, the entry is stepped off, so Back on the login
   * screen afterwards does not land on a page that looks the same.
   */
  guard(on: boolean): void {
    if (on === this.armed) return;
    this.armed = on;
    if (on) {
      this.source.addEventListener("popstate", this.popped);
      if (!isGuard(this.history.state)) this.history.pushState({ [GUARD]: true }, "");
    } else {
      this.source.removeEventListener("popstate", this.popped);
      if (isGuard(this.history.state)) this.history.back();
    }
  }

  private popped = (e: { state: unknown }) => {
    if (!this.armed) return;
    // Forward onto the guard again, after a Back that something else
    // handled: nothing to do.
    if (isGuard(e.state)) return;
    this.history.pushState({ [GUARD]: true }, "");
    if (!this.closeTop()) this.onIdle?.();
  };
}

// ----- the page's own stack -----

let stack: BackStack | null = null;
let inWorld = false;

function page(): BackStack {
  if (!stack) {
    stack = new BackStack(window.history, window as unknown as PopSource);
    onDeviceChange(() => stack?.guard(inWorld && device().touch));
  }
  return stack;
}

/** Put something that Back should close on top of the stack. */
export function pushLayer(close: () => void): Layer {
  return page().push(close);
}

/** Close the top layer, as Back does: for a '‹' button or the Escape key. Whether there was one. */
export function closeTopLayer(): boolean {
  return page().closeTop();
}

/** Be told whenever a sheet, a window, the pie or a menu opens and goes on the stack. Returns a way to stop. */
export function onLayerOpen(fn: () => void): () => void {
  return page().onPush(fn);
}

/** How many layers are open. */
export function openLayers(): number {
  return stack?.size ?? 0;
}

/**
 * Say whether someone is in the world. The guard is up while they are and
 * the screen is a touch screen, and comes down at logout or when the
 * screen stops being one.
 */
export function guardBack(world: boolean): void {
  inWorld = world;
  page().guard(world && device().touch);
}

/** What Back does when nothing is open: ask whether to leave, say. Null for nothing. */
export function setIdleBack(fn: (() => void) | null): void {
  page().onIdle = fn;
}
